/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  FormDialog,
  FormDialogActions,
  FormDialogContent,
  FormDialogTitle,
  FormElement,
  FormInputsStack,
  FormCancelButton,
  Inputs,
  getFieldProps,
} from '@marageti/z4-lib';
import { useForm, Controller } from 'react-hook-form';
import { getDaysInMonth } from 'date-fns';
import { TravelerProfile, TravelerProfileDetailsUpdate } from '@marageti/z4-sdk/lib/people';
import CountrySelector from '../country-lookup-input';
import {
  birthdayStringToDate,
  YEAR_UNKNOWN_SENTINEL,
} from '../traveler-details/traveler-personal-details.utils';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface FormValues {
  preferredName: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  birthdayMonth: string;
  birthdayDay: string;
  birthdayYear: string;
}

interface EditTravelerPersonalDetailsFormProps {
  open: boolean;
  onClose: () => void;
  travelerProfile: TravelerProfile;
  updateTravelerProfile: (
    updates: Partial<TravelerProfileDetailsUpdate>,
    errorMessage?: string,
    successMessage?: string,
  ) => Promise<TravelerProfile | undefined>;
}

const getBirthdayDefaults = (birthday: string | undefined): Pick<FormValues, 'birthdayMonth' | 'birthdayDay' | 'birthdayYear'> => {
  const date = birthdayStringToDate(birthday);
  if (!date) return { birthdayMonth: '', birthdayDay: '', birthdayYear: '' };
  return {
    birthdayMonth: String(date.getMonth() + 1),
    birthdayDay: String(date.getDate()),
    birthdayYear: date.getFullYear() !== YEAR_UNKNOWN_SENTINEL ? String(date.getFullYear()) : '',
  };
};

export const EditTravelerPersonalDetailsForm = ({
  open,
  onClose,
  travelerProfile,
  updateTravelerProfile,
}: EditTravelerPersonalDetailsFormProps) => {
  const { personalDetails } = travelerProfile;

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    trigger,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      preferredName: personalDetails?.preferredName ?? '',
      street1: personalDetails?.residence?.street1 ?? '',
      street2: personalDetails?.residence?.street2 ?? '',
      city: personalDetails?.residence?.city ?? '',
      state: personalDetails?.residence?.state ?? '',
      zipcode: personalDetails?.residence?.zipcode ?? '',
      country: personalDetails?.residence?.country ?? '',
      ...getBirthdayDefaults(personalDetails?.birthday),
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: FormValues) => {
    const hasBirthday = data.birthdayMonth && data.birthdayDay;
    let birthday: string;
    if (hasBirthday) {
      const year = data.birthdayYear ? parseInt(data.birthdayYear, 10) : YEAR_UNKNOWN_SENTINEL;
      // Use Date.UTC to avoid local timezone shifting the date, strip milliseconds
      // so the format matches Go's RFC3339 expectation (time.Time.UnmarshalJSON).
      birthday = new Date(Date.UTC(
        year,
        parseInt(data.birthdayMonth, 10) - 1,
        parseInt(data.birthdayDay, 10),
      )).toISOString().replace(/\.\d{3}Z$/, 'Z');
    } else {
      // Go zero time — signals "no birthday set" to the backend
      birthday = '0001-01-01T00:00:00Z';
    }

    const updated = await updateTravelerProfile(
      {
        personalDetails: {
          preferredName: data.preferredName,
          residence: {
            street1: data.street1,
            street2: data.street2,
            city: data.city,
            state: data.state,
            zipcode: data.zipcode,
            country: data.country,
          },
          birthday,
        },
      },
      'Failed to update traveler personal details.',
      'Traveler personal details updated.',
    );
    if (updated) {
      onClose();
    }
  };

  const currentYear = new Date().getFullYear();

  const watchMonth = watch('birthdayMonth');
  const watchYear = watch('birthdayYear');

  // Use a non-leap year as a safe default when year is unknown (gives Feb 28 days).
  // When a specific year is known, getDaysInMonth correctly handles leap years.
  const maxDay = useMemo(() => {
    if (!watchMonth) return 31;
    const month = parseInt(watchMonth, 10);
    const year = watchYear ? parseInt(watchYear, 10) : 2001;
    return getDaysInMonth(new Date(year, month - 1, 1));
  }, [watchMonth, watchYear]);

  return (
    <FormDialog open={open}>
      <FormDialogTitle handleClose={handleClose}>Personal Details</FormDialogTitle>
      <FormElement name="traveler-personal-details-editor-form" onSubmit={handleSubmit(onSubmit)}>
        <FormDialogContent>
          <FormInputsStack>
            <Controller
              control={control}
              name="preferredName"
              render={({ field }) => (
                <Inputs.Text
                  {...field}
                  id="preferred-name"
                  error={!!errors?.preferredName}
                  helperText={errors?.preferredName?.message}
                  label="Preferred Name"
                />
              )}
            />
            <Controller
              control={control}
              name="street1"
              render={({ field }) => (
                <Inputs.Text
                  {...field}
                  id="street1"
                  error={!!errors?.street1}
                  helperText={errors?.street1?.message}
                  label="Address"
                />
              )}
            />
            <Controller
              control={control}
              name="street2"
              render={({ field }) => (
                <Inputs.Text
                  {...field}
                  id="street2"
                  error={!!errors?.street2}
                  helperText={errors?.street2?.message}
                  label="Address Line 2"
                />
              )}
            />
            <Controller
              control={control}
              name="city"
              render={({ field }) => (
                <Inputs.Text
                  {...field}
                  id="city"
                  error={!!errors?.city}
                  helperText={errors?.city?.message}
                  label="City"
                />
              )}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  control={control}
                  name="state"
                  render={({ field }) => (
                    <Inputs.Text
                      {...field}
                      id="state"
                      error={!!errors?.state}
                      helperText={errors?.state?.message}
                      label="State / Province"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  control={control}
                  name="zipcode"
                  render={({ field }) => (
                    <Inputs.Text
                      {...field}
                      id="zipcode"
                      error={!!errors?.zipcode}
                      helperText={errors?.zipcode?.message}
                      label="Postal / Zip Code"
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <CountrySelector
                  {...field}
                  {...getFieldProps('country', errors)}
                  label="Country"
                  onChange={field.onChange}
                />
              )}
            />
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: -1 }}>
                Birthday
              </Typography>
              <Box display="flex" gap={1}>
                <Box flex={5}>
                  <Controller
                    control={control}
                    name="birthdayMonth"
                    rules={{
                      validate: (v) => {
                        const { birthdayDay, birthdayYear } = getValues();
                        if (!v && (birthdayDay || birthdayYear)) return 'Required';
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        id="birthday-month"
                        label="Month"
                        size="small"
                        fullWidth
                        error={!!errors.birthdayMonth}
                        helperText={errors.birthdayMonth?.message}
                        onChange={(e) => {
                          field.onChange(e);
                          trigger('birthdayDay');
                        }}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {MONTHS.map((month, i) => (
                          <MenuItem key={month} value={String(i + 1)}>
                            {month}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>
                <Box flex={3}>
                  <Controller
                    control={control}
                    name="birthdayDay"
                    rules={{
                      validate: (v) => {
                        const { birthdayMonth, birthdayYear } = getValues();
                        if (!v && (birthdayMonth || birthdayYear)) return 'Required';
                        if (v) {
                          const n = parseInt(v, 10);
                          if (!Number.isInteger(n) || n < 1) return 'Invalid';
                          if (birthdayMonth) {
                            const year = birthdayYear ? parseInt(birthdayYear, 10) : 2001;
                            const daysInMonth = getDaysInMonth(new Date(year, parseInt(birthdayMonth, 10) - 1, 1));
                            if (n > daysInMonth) return `Max ${daysInMonth} for this month`;
                          } else if (n > 31) {
                            return 'Invalid';
                          }
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="birthday-day"
                        label="Day"
                        type="number"
                        size="small"
                        fullWidth
                        inputProps={{ min: 1, max: maxDay }}
                        error={!!errors.birthdayDay}
                        helperText={errors.birthdayDay?.message}
                        onChange={(e) => {
                          field.onChange(e);
                          trigger('birthdayMonth');
                        }}
                      />
                    )}
                  />
                </Box>
                <Box flex={4}>
                  <Controller
                    control={control}
                    name="birthdayYear"
                    rules={{
                      validate: (v) => {
                        if (!v) return true;
                        const n = parseInt(v, 10);
                        return (Number.isInteger(n) && n >= 1900 && n <= currentYear) || 'Invalid year';
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="birthday-year"
                        label="Year"
                        type="number"
                        size="small"
                        fullWidth
                        error={!!errors.birthdayYear}
                        helperText={errors.birthdayYear?.message ?? 'Optional'}
                        onChange={(e) => {
                          field.onChange(e);
                          trigger(['birthdayMonth', 'birthdayDay', 'birthdayYear']);
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
          </FormInputsStack>
        </FormDialogContent>
        <FormDialogActions>
          <FormCancelButton onClick={handleClose} />
          <LoadingButton
            variant="contained"
            type="submit"
            loading={isSubmitting}
            disabled={!isValid}
          >
            Save
          </LoadingButton>
        </FormDialogActions>
      </FormElement>
    </FormDialog>
  );
};

export default EditTravelerPersonalDetailsForm;
